/**
 * Gamification API
 *
 * Handles XP tracking, badge management, and leaderboards.
 */

import { mockDb } from './mockDb'
import type {
  Badge,
  UserBadge,
  XPEvent,
  XPEventType,
  LeaderboardEntry,
  LeaderboardScope,
  LeaderboardType,
  GamificationStats,
  GroupChallenge,
  BadgeProgress,
} from '../types'

// Simulate API delay
const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms))

// Session key (shared)
const SESSION_KEY = 'docurious-session'

function getCurrentUserId(): string {
  const stored = localStorage.getItem(SESSION_KEY)
  if (!stored) return 'anonymous'
  try {
    return JSON.parse(stored).userId ?? 'anonymous'
  } catch {
    return 'anonymous'
  }
}

// Default badges
const defaultBadges: Badge[] = [
  {
    id: 'badge-first-challenge',
    name: 'First Steps',
    description: 'Complete your first challenge',
    iconUrl: '/badges/first-steps.svg',
    category: 'milestone',
    rarity: 'common',
    criteria: { type: 'challenge_count', value: 1 },
    xpBonus: 50,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
  {
    id: 'badge-five-challenges',
    name: 'Getting Started',
    description: 'Complete 5 challenges',
    iconUrl: '/badges/getting-started.svg',
    category: 'milestone',
    rarity: 'uncommon',
    criteria: { type: 'challenge_count', value: 5 },
    xpBonus: 100,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
  {
    id: 'badge-ten-challenges',
    name: 'Challenge Seeker',
    description: 'Complete 10 challenges',
    iconUrl: '/badges/challenge-seeker.svg',
    category: 'milestone',
    rarity: 'rare',
    criteria: { type: 'challenge_count', value: 10 },
    xpBonus: 200,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
  {
    id: 'badge-streak-2',
    name: 'Getting Going',
    description: 'Maintain a 2-week activity streak',
    iconUrl: '/badges/getting-going.svg',
    category: 'streak',
    rarity: 'common',
    criteria: { type: 'streak', value: 2 },
    xpBonus: 25,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
  {
    id: 'badge-streak-4',
    name: 'Week Warrior',
    description: 'Maintain a 4-week activity streak',
    iconUrl: '/badges/week-warrior.svg',
    category: 'streak',
    rarity: 'uncommon',
    criteria: { type: 'streak', value: 4 },
    xpBonus: 75,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
  {
    id: 'badge-streak-12',
    name: 'Dedicated Learner',
    description: 'Maintain a 12-week activity streak',
    iconUrl: '/badges/dedicated-learner.svg',
    category: 'streak',
    rarity: 'rare',
    criteria: { type: 'streak', value: 12 },
    xpBonus: 200,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
  {
    id: 'badge-streak-26',
    name: 'Half-Year Hero',
    description: 'Maintain a 26-week activity streak',
    iconUrl: '/badges/half-year-hero.svg',
    category: 'streak',
    rarity: 'epic',
    criteria: { type: 'streak', value: 26 },
    xpBonus: 300,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
  {
    id: 'badge-outdoor-explorer',
    name: 'Outdoor Explorer',
    description: 'Complete 3 Outdoor Adventure challenges',
    iconUrl: '/badges/outdoor-explorer.svg',
    category: 'category_mastery',
    rarity: 'uncommon',
    criteria: { type: 'category_count', value: 3, categoryId: 'outdoor-adventure' },
    xpBonus: 100,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
  {
    id: 'badge-master-chef',
    name: 'Master Chef',
    description: 'Complete 5 Cooking & Nutrition challenges',
    iconUrl: '/badges/master-chef.svg',
    category: 'category_mastery',
    rarity: 'rare',
    criteria: { type: 'category_count', value: 5, categoryId: 'cooking-nutrition' },
    xpBonus: 150,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
  {
    id: 'badge-creative-soul',
    name: 'Creative Soul',
    description: 'Complete 5 Decorative Arts & Crafts challenges',
    iconUrl: '/badges/creative-soul.svg',
    category: 'category_mastery',
    rarity: 'rare',
    criteria: { type: 'category_count', value: 5, categoryId: 'decorative-arts' },
    xpBonus: 150,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
  {
    id: 'badge-community-hero',
    name: 'Community Hero',
    description: 'Help verify 10 track records',
    iconUrl: '/badges/community-hero.svg',
    category: 'cooperation',
    rarity: 'rare',
    criteria: { type: 'verification_count', value: 10 },
    xpBonus: 200,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
  {
    id: 'badge-early-adopter',
    name: 'Early Adopter',
    description: 'Join during the beta period',
    iconUrl: '/badges/early-adopter.svg',
    category: 'special',
    rarity: 'legendary',
    criteria: { type: 'special', value: 0 },
    xpBonus: 500,
    isHidden: true,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
]

// Initialize badges in mockDb
function initializeBadges() {
  const existingBadges = mockDb.getAll<Badge>('badges')
  if (existingBadges.length === 0) {
    defaultBadges.forEach((badge) => mockDb.insertWithId('badges', badge))
  }
}

/**
 * Get all available badges
 */
export async function getAllBadges(): Promise<Badge[]> {
  await delay(200)
  initializeBadges()
  return mockDb.getAll<Badge>('badges')
}

/**
 * Get a user's earned badges
 */
export async function getUserBadges(userId: string): Promise<UserBadge[]> {
  await delay(200)
  const allUserBadges = mockDb.getAll<UserBadge>('userBadges')
  return allUserBadges.filter((ub) => ub.userId === userId)
}

/**
 * Award a badge to a user
 */
export async function awardBadge(
  userId: string,
  badgeId: string,
  metadata?: Record<string, unknown>
): Promise<UserBadge | null> {
  await delay(300)

  // Check if user already has this badge
  const existingBadges = mockDb.getAll<UserBadge>('userBadges')
  const alreadyEarned = existingBadges.find(
    (ub) => ub.userId === userId && ub.badgeId === badgeId
  )
  if (alreadyEarned) {
    return null
  }

  // Get badge details
  const badge = mockDb.findById<Badge>('badges', badgeId)
  if (!badge) {
    return null
  }

  const userBadge: UserBadge = {
    id: `ub-${Date.now()}`,
    userId,
    badgeId,
    badge,
    earnedAt: new Date().toISOString(),
    metadata,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  }

  mockDb.insertWithId('userBadges', userBadge)

  // Award XP bonus from badge
  if (badge.xpBonus) {
    await addXPEvent(userId, 'badge_earned', badge.xpBonus, {
      badgeId,
      badgeName: badge.name,
    })
  }

  return userBadge
}

/**
 * Get XP events for a user
 */
export async function getXPEvents(
  userId: string,
  limit?: number
): Promise<XPEvent[]> {
  await delay(200)
  const allEvents = mockDb.getAll<XPEvent>('xpEvents')
  const userEvents = allEvents
    .filter((e) => e.userId === userId)
    .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())

  return limit ? userEvents.slice(0, limit) : userEvents
}

/**
 * Add an XP event
 */
export async function addXPEvent(
  userId: string,
  eventType: XPEventType,
  amount: number,
  metadata?: Record<string, unknown>
): Promise<XPEvent> {
  await delay(200)

  const now = new Date()
  const dayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate()).toISOString()

  // Anti-gaming: daily XP cap check
  const allEvents = mockDb.getAll<XPEvent>('xpEvents')
  const todayXp = allEvents
    .filter((e) => e.userId === userId && e.createdAt >= dayStart)
    .reduce((sum, e) => sum + e.amount, 0)

  const dailyCap = 500
  const cappedAmount = Math.min(amount, Math.max(0, dailyCap - todayXp))

  // Anti-gaming: duplicate completion detection within 24 hours
  if (eventType === 'challenge_completed' && metadata?.challengeId) {
    const twentyFourHoursAgo = new Date(now.getTime() - 24 * 60 * 60 * 1000).toISOString()
    const recentDuplicate = allEvents.find(
      (e) =>
        e.userId === userId &&
        e.eventType === 'challenge_completed' &&
        e.createdAt >= twentyFourHoursAgo &&
        (e.metadata as Record<string, unknown> | undefined)?.challengeId === metadata.challengeId
    )
    if (recentDuplicate) {
      const xpEvent: XPEvent = {
        id: `xp-${Date.now()}`,
        userId,
        eventType,
        amount: 0,
        metadata: { ...metadata, capped: true, reason: 'duplicate_within_24h' },
        createdAt: now.toISOString(),
        updatedAt: now.toISOString(),
      }
      mockDb.insertWithId('xpEvents', xpEvent)
      return xpEvent
    }
  }

  const xpEvent: XPEvent = {
    id: `xp-${Date.now()}`,
    userId,
    eventType,
    amount: cappedAmount,
    metadata: cappedAmount < amount ? { ...metadata, capped: true, originalAmount: amount } : metadata,
    createdAt: now.toISOString(),
    updatedAt: now.toISOString(),
  }

  mockDb.insertWithId('xpEvents', xpEvent)
  return xpEvent
}

/**
 * Get total XP for a user
 */
export async function getUserTotalXP(userId: string): Promise<number> {
  await delay(100)
  const events = mockDb.getAll<XPEvent>('xpEvents')
  return events
    .filter((e) => e.userId === userId)
    .reduce((sum, e) => sum + e.amount, 0)
}

/**
 * Get gamification stats for a user
 */
export async function getGamificationStats(userId: string): Promise<GamificationStats> {
  await delay(300)

  const totalXp = await getUserTotalXP(userId)
  const userBadges = await getUserBadges(userId)
  const recentXpEvents = await getXPEvents(userId, 10)

  // Calculate streak (consecutive weeks with activity per Doc 8)
  const events = mockDb.getAll<XPEvent>('xpEvents')
  const userEvents = events.filter((e) => e.userId === userId)
  const currentStreak = calculateStreak(userEvents)

  return {
    totalXp,
    currentStreak,
    longestStreak: currentStreak, // Simplified
    badgesEarned: userBadges.length,
    challengesCompleted: userEvents.filter((e) => e.eventType === 'challenge_completed').length,
    recentXpEvents,
  }
}

/**
 * Get the ISO week number for a date (Mon-Sun weeks)
 */
function getWeekKey(date: Date): string {
  const d = new Date(date)
  d.setHours(0, 0, 0, 0)
  // Set to nearest Thursday: current date + 4 - current day number (Mon=1, Sun=7)
  d.setDate(d.getDate() + 4 - (d.getDay() || 7))
  const yearStart = new Date(d.getFullYear(), 0, 1)
  const weekNo = Math.ceil(((d.getTime() - yearStart.getTime()) / 86400000 + 1) / 7)
  return `${d.getFullYear()}-W${weekNo}`
}

/**
 * Calculate current weekly streak from XP events.
 * Per Doc 8: streaks are measured in consecutive weeks with activity.
 */
function calculateStreak(events: XPEvent[]): number {
  if (events.length === 0) return 0

  // Get unique weeks with activity
  const weekKeys = [...new Set(events.map((e) => getWeekKey(new Date(e.createdAt))))]
    .sort()
    .reverse()

  if (weekKeys.length === 0) return 0

  const currentWeek = getWeekKey(new Date())
  const lastWeekDate = new Date()
  lastWeekDate.setDate(lastWeekDate.getDate() - 7)
  const lastWeek = getWeekKey(lastWeekDate)

  // Check if there's activity this week or last week
  if (weekKeys[0] !== currentWeek && weekKeys[0] !== lastWeek) {
    return 0
  }

  // Count consecutive weeks backward
  let streak = 1
  for (let i = 1; i < weekKeys.length; i++) {
    // Parse week keys to check consecutive-ness
    const prevWeekDate = new Date(weekKeys[i - 1].replace(/-W/, '-01-01'))
    const currWeekDate = new Date(weekKeys[i].replace(/-W/, '-01-01'))
    // Simpler: just check if previous entry is exactly 1 week prior
    const prevParts = weekKeys[i - 1].split('-W').map(Number)
    const currParts = weekKeys[i].split('-W').map(Number)

    const isConsecutive =
      (prevParts[0] === currParts[0] && prevParts[1] === currParts[1] + 1) ||
      // Handle year boundary (last week of year → first week of next year)
      (prevParts[0] === currParts[0] + 1 && prevParts[1] === 1 && currParts[1] >= 52)

    void prevWeekDate
    void currWeekDate

    if (isConsecutive) {
      streak++
    } else {
      break
    }
  }

  return streak
}

/**
 * Get leaderboard entries
 */
export async function getLeaderboard(
  type: LeaderboardType = 'xp',
  limit: number = 10,
  scope: LeaderboardScope = 'global'
): Promise<LeaderboardEntry[]> {
  await delay(300)
  void scope // Scope filtering would be applied with real backend data

  // Get all users
  const users = mockDb.getAll<{ id: string; displayName: string; avatarUrl?: string }>('users')
  const xpEvents = mockDb.getAll<XPEvent>('xpEvents')
  const userBadges = mockDb.getAll<UserBadge>('userBadges')

  const entries: LeaderboardEntry[] = await Promise.all(
    users.map(async (user) => {
      const userXpEvents = xpEvents.filter((e) => e.userId === user.id)
      const totalXp = userXpEvents.reduce((sum, e) => sum + e.amount, 0)
      const badges = userBadges.filter((ub) => ub.userId === user.id)
      const streak = calculateStreak(userXpEvents)
      const completedChallenges = userXpEvents.filter(
        (e) => e.eventType === 'challenge_completed'
      ).length

      let value = totalXp
      if (type === 'challenges') value = completedChallenges
      if (type === 'streak') value = streak
      if (type === 'badges') value = badges.length
      if (type === 'community') value = totalXp // community contributions proxy

      return {
        userId: user.id,
        displayName: user.displayName || 'Anonymous',
        avatarUrl: user.avatarUrl,
        value,
        rank: 0,
        totalXp,
        badgeCount: badges.length,
      }
    })
  )

  // Sort and assign ranks
  entries.sort((a, b) => b.value - a.value)
  entries.forEach((entry, index) => {
    entry.rank = index + 1
  })

  return entries.slice(0, limit)
}

/**
 * Check and award badges based on user's progress
 */
export async function checkAndAwardBadges(userId: string): Promise<UserBadge[]> {
  await delay(300)
  initializeBadges()

  const earnedBadges: UserBadge[] = []
  const badges = mockDb.getAll<Badge>('badges')
  const existingUserBadges = mockDb.getAll<UserBadge>('userBadges').filter(
    (ub) => ub.userId === userId
  )
  const earnedBadgeIds = new Set(existingUserBadges.map((ub) => ub.badgeId))

  const xpEvents = mockDb.getAll<XPEvent>('xpEvents').filter((e) => e.userId === userId)
  const completedChallenges = xpEvents.filter(
    (e) => e.eventType === 'challenge_completed'
  ).length
  const streak = calculateStreak(xpEvents)

  for (const badge of badges) {
    if (earnedBadgeIds.has(badge.id)) continue

    let shouldAward = false

    switch (badge.criteria.type) {
      case 'challenge_count':
        shouldAward = completedChallenges >= badge.criteria.value
        break
      case 'streak':
        shouldAward = streak >= badge.criteria.value
        break
      // Add more criteria types as needed
    }

    if (shouldAward) {
      const awarded = await awardBadge(userId, badge.id)
      if (awarded) earnedBadges.push(awarded)
    }
  }

  return earnedBadges
}

/**
 * Get category counts for a user — number of completed challenges per category slug.
 * Queries completed userChallenges joined with challenges to count per category.
 */
export async function getCategoryCounts(userId: string): Promise<Record<string, number>> {
  await delay(200)

  // Find all userChallenges for this user that are completed/verified
  const userChallenges = mockDb.findWhere<{
    userId: string
    challengeId: string
    status: string
  }>('userChallenges', (uc) => uc.userId === userId && (uc.status === 'completed' || uc.status === 'verified'))

  const counts: Record<string, number> = {}

  for (const uc of userChallenges) {
    const challenge = mockDb.findById<{
      categoryIds?: string[]
      categories?: { id: string; slug?: string; name?: string }[]
    }>('challenges', uc.challengeId)

    if (!challenge) continue

    // Try to get category slug from populated categories, or use categoryIds
    if (challenge.categories && challenge.categories.length > 0) {
      for (const cat of challenge.categories) {
        const slug = cat.slug ?? cat.id
        counts[slug] = (counts[slug] ?? 0) + 1
      }
    } else if (challenge.categoryIds && challenge.categoryIds.length > 0) {
      for (const catId of challenge.categoryIds) {
        counts[catId] = (counts[catId] ?? 0) + 1
      }
    }
  }

  return counts
}

/**
 * Get progress toward all unearned badges for a user.
 */
export async function getBadgeProgress(userId: string): Promise<BadgeProgress[]> {
  await delay(200)
  initializeBadges()

  const badges = mockDb.getAll<Badge>('badges')
  const existingUserBadges = mockDb.getAll<UserBadge>('userBadges').filter(
    (ub) => ub.userId === userId
  )
  const earnedBadgeIds = new Set(existingUserBadges.map((ub) => ub.badgeId))

  const xpEvents = mockDb.getAll<XPEvent>('xpEvents').filter((e) => e.userId === userId)
  const completedChallenges = xpEvents.filter(
    (e) => e.eventType === 'challenge_completed'
  ).length
  const streak = calculateStreak(xpEvents)
  const verifications = xpEvents.filter(
    (e) => e.eventType === 'verification_given'
  ).length

  // Get category counts
  const categoryCounts = await getCategoryCounts(userId)

  const progress: BadgeProgress[] = []

  for (const badge of badges) {
    if (earnedBadgeIds.has(badge.id)) continue
    if (badge.isHidden) continue

    let current = 0
    const target = badge.criteria.value

    switch (badge.criteria.type) {
      case 'challenge_count':
        current = completedChallenges
        break
      case 'category_count':
        current = badge.criteria.categoryId
          ? (categoryCounts[badge.criteria.categoryId] ?? 0)
          : 0
        break
      case 'streak':
        current = streak
        break
      case 'verification_count':
        current = verifications
        break
      case 'special':
        continue // Can't track progress for special badges
    }

    if (target > 0) {
      progress.push({
        badgeId: badge.id,
        badge,
        current: Math.min(current, target),
        target,
        percentage: Math.min(100, Math.round((current / target) * 100)),
      })
    }
  }

  // Sort by percentage (closest to earning first)
  progress.sort((a, b) => b.percentage - a.percentage)

  return progress
}

// ============================================================================
// Group Challenges (cooperative/social gamification)
// ============================================================================

/**
 * Create a new group challenge
 */
export async function createGroupChallenge(challengeId: string, name: string): Promise<GroupChallenge> {
  await delay(300)
  const session = getCurrentUserId()
  return {
    id: `gc-${Date.now()}`,
    challengeId,
    creatorUserId: session,
    name,
    memberUserIds: [session],
    status: 'open',
    teamXpMultiplier: 1.0,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  }
}

/**
 * Join an existing group challenge
 */
export async function joinGroupChallenge(groupId: string): Promise<GroupChallenge> {
  await delay(200)
  void groupId
  return {
    id: groupId,
    challengeId: '',
    creatorUserId: '',
    name: '',
    memberUserIds: [],
    status: 'open',
    teamXpMultiplier: 1.25,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  }
}

/**
 * Get all group challenges the current user is part of
 */
export async function getGroupChallenges(): Promise<GroupChallenge[]> {
  await delay(200)
  return []
}
